import { useState, useRef, useCallback } from 'react';
import { Upload, Image, X } from 'lucide-react';

export default function DragDropUpload({ 
    onFilesSelected, 
    accept = "image/*", 
    multiple = true,
    disabled = false,
    children,
    className = "",
    compact = false,
    maxSizeMB = null,
    onError = null,
    "data-testid": dataTestId
}) {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);

    const validateSize = (files) => {
        if (!maxSizeMB) return { valid: files, errors: [] };
        const maxBytes = maxSizeMB * 1024 * 1024;
        const valid = [];
        const errors = [];
        files.forEach((f) => {
            if (f.size > maxBytes) {
                const sizeMB = (f.size / 1024 / 1024).toFixed(1);
                errors.push(`${f.name}: ${sizeMB} MB (max ${maxSizeMB} MB)`);
            } else {
                valid.push(f);
            }
        });
        if (errors.length && onError) {
            onError(`File troppo grandi, saltati:\n${errors.join('\n')}`);
        }
        return { valid, errors };
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragIn = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, []);

    const handleDragOut = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            // Estensioni comuni per fallback (quando file.type è vuoto, es. Firefox su alcuni OS)
            const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif', '.tif', '.tiff', '.svg', '.avif'];
            const VIDEO_EXT = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v', '.3gp', '.wmv', '.flv', '.mpeg', '.mpg'];

            const getExt = (name) => {
                if (!name) return '';
                const i = name.lastIndexOf('.');
                return i >= 0 ? name.slice(i).toLowerCase() : '';
            };

            // Filtra i file in base all'accept
            const acceptedTypes = accept.split(',').map(t => t.trim());
            const validFiles = Array.from(files).filter(file => {
                const ext = getExt(file.name);
                return acceptedTypes.some(type => {
                    if (type === 'image/*') {
                        return file.type.startsWith('image/') || IMAGE_EXT.includes(ext);
                    }
                    if (type === 'video/*') {
                        return file.type.startsWith('video/') || VIDEO_EXT.includes(ext);
                    }
                    if (type === '.pdf') {
                        return file.type === 'application/pdf' || ext === '.pdf';
                    }
                    // Se type inizia con "." -> confronto estensione
                    if (type.startsWith('.')) return ext === type.toLowerCase();
                    return file.type === type;
                });
            });

            if (validFiles.length === 0) {
                // Nessun file valido: avvisa l'utente invece di fallire in silenzio
                if (onError) {
                    const names = Array.from(files).map(f => f.name || '(senza nome)').join(', ');
                    onError(`Nessun file compatibile trovato: ${names}. Tipi accettati: ${accept}`);
                }
                return;
            }

            const { valid } = validateSize(validFiles);
            if (valid.length > 0) {
                onFilesSelected(multiple ? valid : [valid[0]]);
            }
        }
    }, [accept, disabled, multiple, onFilesSelected, maxSizeMB, onError]);

    const handleFileInput = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const { valid } = validateSize(Array.from(files));
            if (valid.length > 0) {
                onFilesSelected(valid);
            }
        }
        // Reset input per permettere di selezionare lo stesso file
        e.target.value = '';
    };

    const openFileDialog = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (compact) {
        // Versione compatta - solo area cliccabile con drag support
        return (
            <div
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={openFileDialog}
                data-testid={dataTestId}
                className={`cursor-pointer transition-all ${isDragging ? 'ring-2 ring-[#00FF41] bg-[#00FF41]/10' : ''} ${className}`}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleFileInput}
                    className="hidden"
                    disabled={disabled}
                />
                {children}
            </div>
        );
    }

    return (
        <div
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={openFileDialog}
            data-testid={dataTestId}
            className={`
                relative border-2 border-dashed transition-all cursor-pointer
                ${isDragging 
                    ? 'border-[#00FF41] bg-[#00FF41]/10' 
                    : 'border-white/20 hover:border-white/40'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${className}
            `}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileInput}
                className="hidden"
                disabled={disabled}
            />
            
            {children ? children : (
                <div className="p-6 text-center">
                    <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-[#00FF41]' : 'text-white/40'}`} />
                    <p className={`text-sm ${isDragging ? 'text-[#00FF41]' : 'text-white/60'}`}>
                        {isDragging 
                            ? 'Rilascia qui i file' 
                            : 'Trascina i file qui o clicca per selezionare'
                        }
                    </p>
                    <p className="text-xs text-white/40 mt-1">
                        {accept.includes('image') && 'Immagini'}
                        {accept.includes('video') && ', Video'}
                        {accept.includes('.pdf') && ', PDF'}
                    </p>
                </div>
            )}

            {/* Overlay durante il drag */}
            {isDragging && (
                <div className="absolute inset-0 bg-[#00FF41]/5 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/80 px-4 py-2 border border-[#00FF41]">
                        <p className="text-[#00FF41] font-mono text-sm">RILASCIA QUI</p>
                    </div>
                </div>
            )}
        </div>
    );
}
